import { useState, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Modal from '../../common/Modal';
import ModalButton from '../../common/ModalButton';
import Fireworks from '../../common/Fireworks';
import theme from '../../../utils/theme';

import './WelcomeModal.scss';

// Each tip is under dialogs.welcome.tips
const ADMIN_TIPS = ['create', 'services', 'team', 'transpose', 'autoScroll', 'filter', 'darkMode'];
const MEMBER_TIPS = ['browse', 'transpose', 'handsFree', 'favorites', 'darkMode'];

const WelcomeModal = ({ isOpen, onClose, welcomeModalData }) => {
  const { t } = useTranslation();
  const [isClosing, setIsClosing] = useState(false);
  const { firstName, teamName, teamMembers = [], isAdmin = false } = welcomeModalData;

  // A first visit starts in light, whatever an earlier account on this device left switched on
  useLayoutEffect(() => {
    if (isOpen) theme.removeDarkTheme();
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 500); // Match animation duration
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title=""
      showCloseButton={false}
      closeOnBackdropClick={false}
      opaqueBackdrop={true}
      showLogo={true}
      className={`welcome-modal-container ${isClosing ? 'closing' : ''}`}
      fireworksContent={
        <div className="fireworks-wrapper">
          <Fireworks count={10} particlesPerFirework={12} />
        </div>
      }
      footer={
        <ModalButton block onClick={handleClose}>
          {t('dialogs.welcome.start')}
        </ModalButton>
      }
    >
      <div className="WelcomeModal">
        <h2 className="greeting">{t('dialogs.welcome.hi', { name: firstName })}</h2>
        <p className="welcome-text">{t('dialogs.welcome.welcomeTo', { team: teamName })}</p>
        <p className="member-count">{t('dialogs.welcome.memberCount', { count: teamMembers?.length })}</p>

        {teamMembers.length > 0 && (
          <div className="team-members-section">
            <div className="team-members-scroll horizontal-menu">
              {teamMembers.map((member, index) => (
                <div key={index} className="team-member-chip">
                  {member.name
                    ? member.name.charAt(0).toUpperCase() + member.name.slice(1).toLowerCase()
                    : member.firstName}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="tips-section">
          <p className="tips-title">
            {t('dialogs.welcome.tipsTitle')}
            <br />
            {t('dialogs.welcome.tipsTitleEnd')}
          </p>
          <ul className="tips-list">
            {(isAdmin ? ADMIN_TIPS : MEMBER_TIPS).map((tip) => (
              <li key={tip}>{t(`dialogs.welcome.tips.${tip}`)}</li>
            ))}
          </ul>
        </div>
      </div>
    </Modal>
  );
};

WelcomeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  welcomeModalData: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    teamName: PropTypes.string.isRequired,
    isAdmin: PropTypes.bool,
    teamMembers: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        firstName: PropTypes.string,
      }),
    ),
  }).isRequired,
};

export default WelcomeModal;
